import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit, Trash2, ExternalLink, GripVertical } from "lucide-react";

const QuickLinksManager = () => {
  const [links, setLinks] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState({ label: "", url: "", is_active: true, order: 0 });

  const fetchLinks = async () => {
    try {
      const res = await axios.get(`${API}/quick-links`, { withCredentials: true });
      setLinks(res.data);
    } catch (e) {
      console.error("Failed to fetch quick links:", e);
    }
  };

  useEffect(() => { fetchLinks(); }, []);

  const openDialog = (link = null) => {
    if (link) {
      setEditingLink(link);
      setFormData({ label: link.label, url: link.url, is_active: link.is_active, order: link.order || 0 });
    } else {
      setEditingLink(null);
      setFormData({ label: "", url: "", is_active: true, order: links.length });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.label.trim() || !formData.url.trim()) {
      toast.error("Label and URL are required");
      return;
    }
    try {
      if (editingLink) {
        await axios.put(`${API}/quick-links/${editingLink.link_id}`, formData, { withCredentials: true });
        toast.success("Link updated");
      } else {
        await axios.post(`${API}/quick-links`, formData, { withCredentials: true });
        toast.success("Link created");
      }
      setIsDialogOpen(false);
      fetchLinks();
    } catch (e) {
      toast.error("Failed to save link");
    }
  };

  const handleDelete = async (linkId) => {
    if (!window.confirm("Delete this link?")) return;
    try {
      await axios.delete(`${API}/quick-links/${linkId}`, { withCredentials: true });
      toast.success("Link deleted");
      fetchLinks();
    } catch (e) {
      toast.error("Failed to delete link");
    }
  };

  const toggleActive = async (link) => {
    try {
      await axios.put(`${API}/quick-links/${link.link_id}`, { is_active: !link.is_active }, { withCredentials: true });
      fetchLinks();
    } catch (e) {
      toast.error("Failed to update link");
    }
  };

  return (
    <div data-testid="quick-links-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-cfc-primary">Quick Links</h1>
          <p className="text-cfc-text-muted text-sm mt-1">Manage homepage announcement links</p>
        </div>
        <Button
          onClick={() => openDialog()}
          className="bg-cfc-accent hover:bg-cfc-accent-hover text-cfc-primary"
          data-testid="add-quick-link-btn"
        >
          <Plus size={18} className="mr-2" /> Add Link
        </Button>
      </div>

      <div className="space-y-3">
        {links.map((link) => (
          <Card key={link.link_id} className="border-cfc-border" data-testid={`quick-link-${link.link_id}`}>
            <CardContent className="p-4 flex items-center gap-4">
              <GripVertical size={16} className="text-cfc-text-muted flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`font-medium ${link.is_active ? "text-cfc-primary" : "text-cfc-text-muted line-through"}`}>
                    {link.label}
                  </span>
                  {!link.is_active && (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">Inactive</span>
                  )}
                </div>
                <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-sm text-cfc-text-muted hover:text-cfc-accent flex items-center gap-1 truncate">
                  <ExternalLink size={12} /> {link.url}
                </a>
              </div>
              <Switch checked={link.is_active} onCheckedChange={() => toggleActive(link)} />
              <Button variant="ghost" size="sm" onClick={() => openDialog(link)} data-testid={`edit-link-${link.link_id}`}>
                <Edit size={16} />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(link.link_id)} className="text-cfc-destructive" data-testid={`delete-link-${link.link_id}`}>
                <Trash2 size={16} />
              </Button>
            </CardContent>
          </Card>
        ))}
        {links.length === 0 && (
          <div className="text-center py-12 text-cfc-text-muted">
            <p>No quick links yet. Add one to display it on the homepage.</p>
          </div>
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent data-testid="quick-link-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-cfc-primary">
              {editingLink ? "Edit Link" : "New Quick Link"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="ql-label">Label</Label>
              <Input
                id="ql-label"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="e.g. Spring Registration Open"
                data-testid="quick-link-label-input"
              />
            </div>
            <div>
              <Label htmlFor="ql-url">URL</Label>
              <Input
                id="ql-url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://..."
                data-testid="quick-link-url-input"
              />
            </div>
            <div>
              <Label htmlFor="ql-order">Display Order</Label>
              <Input
                id="ql-order"
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                data-testid="quick-link-order-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="ql-active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="ql-active">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleSubmit} className="bg-cfc-primary hover:bg-cfc-primary-hover" data-testid="save-quick-link-btn">
              {editingLink ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuickLinksManager;
